import { useCallback, useEffect, useState, useRef } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  message,
  Row,
  Col,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Switch,
  Typography,
  Select,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, postData, deleteData, putData } from "../../../apiServices/common";
import {
  PACKING_TABLE_ENDPOINT,
  PACKING_TABLE_ADD_ENDPOINT,
  PACKING_TABLE_EDIT_ENDPOINT,
  PACKING_TABLE_DELETE_ENDPOINT,
  PACKING_LINES_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  PACKING_TABLE_CREATE_PERMISSION,
  PACKING_TABLE_EDIT_PERMISSION,
  PACKING_TABLE_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const PackingTableConfig = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [packingLinesList, setPackingLinesList] = useState([]);

  const [warehouseForm] = Form.useForm();
  const { Title } = Typography;
  const { Option } = Select;

  const filterData = useSelector(({ filter }) => filter.filterData);
  const prevFilterDataRef = useRef(filterData);
  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPackingTables = useCallback(async (filterValues, page = null) => {
    const { name } = filterValues || {};
    setLoading(true);
    setFetchError(null);

    let pageToUse;
    if (page !== null) {
      pageToUse = page;
    } else if (filterValues !== null && filterValues !== undefined) {
      pageToUse = 1;
      setCurrentPage(1);
      currentPageRef.current = 1;
    } else {
      pageToUse = currentPageRef.current;
    }

    const query = `${PACKING_TABLE_ENDPOINT}`;
    const bodyData = {
      ...(name && { name: name }),
      page: pageToUse,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200) {
      setLoading(false);
      const pageFromResponse = response?.data?.data?.current_page;
      setCurrentPage(pageFromResponse);
      currentPageRef.current = pageFromResponse;
      setMetaData({
        defaultPageSize: response?.data?.data?.per_page,
        current: pageFromResponse,
        defaultCurrent: pageFromResponse,
        total: response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      setLoading(false);
      setFetchError("Could not load this list.");
      message.error("Error getting garments parts data");
    }
  }, []);

  const getPackingLines = async (searchValue = null) => {
    const query = `${PACKING_LINES_ENDPOINT}`;
    const bodyData = {
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 1,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPackingLinesList(data);
    }
  };

  useEffect(() => {
    getPackingLines();
  }, []);

  useEffect(() => {
    const isFilterChanged = prevFilterDataRef.current !== filterData && prevFilterDataRef.current !== null;

    if (isFilterChanged) {
      setCurrentPage(1);
      currentPageRef.current = 1;

      prevFilterDataRef.current = filterData;

      getAllPackingTables(filterData, 1);
    } else {
      prevFilterDataRef.current = filterData;

      getAllPackingTables(filterData, currentPageRef.current);
    }
  }, [refresh, filterData, getAllPackingTables]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    warehouseForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedPackingTable = async (id) => {
    setLoading(true);
    const response = await deleteData(`${PACKING_TABLE_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Table Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedPackingTable = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      packing_line_id: formValues?.packing_line_id,
      description: formValues?.description || "",
      status: formValues?.status ? "active" : "inactive",
    };

    const response = await putData(`${PACKING_TABLE_EDIT_ENDPOINT}/${formValues?.id}`, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Table Edited Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewPackingTable = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      packing_line_id: formValues?.packing_line_id,
      description: formValues?.description || "",
      status: formValues?.status ? "active" : "inactive",
    };

    const response = await postData(PACKING_TABLE_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Packing Table Created Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = async (record) => {
    setIsEdit(true);
    setLoading(true);
    setIsModalOpen(true);

    try {
      const response = await getData(`${PACKING_TABLE_ENDPOINT}/${record?.id}/edit`);

      if (response?.data?.code === 200) {
        const data = response?.data?.data;

        if (data?.packing_line && data?.packing_line?.id) {
          const packingLineExists = packingLinesList.some((line) => line.id === data.packing_line.id);

          if (!packingLineExists) {
            setPackingLinesList((prevList) => [
              {
                id: data.packing_line.id,
                name: data.packing_line.name,
              },
              ...prevList,
            ]);
          }
        }

        setLoading(false);
        warehouseForm.setFieldsValue({
          id: data?.id,
          name: data?.name,
          packing_line_id: data?.packing_line_id,
          description: data?.description || "",
          status: data?.status === "active" ? true : false,
        });
      } else {
        setLoading(false);
        message.error("Error fetching packing table data");
        setIsModalOpen(false);
        setIsEdit(false);
      }
    } catch (error) {
      setLoading(false);
      message.error("Error fetching packing table data");
      setIsModalOpen(false);
      setIsEdit(false);
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    getAllPackingTables(filterData, newPage);
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: ["name"],
      key: "name",
    },
    {
      title: "Packing Line",
      dataIndex: ["packing_line"],
      key: "packing_line",
      render: (_, record) => {
        return <div>{record?.packing_line?.name || "-"}</div>;
      },
    },
    {
      title: "Description",
      dataIndex: ["description"],
      key: "description",
      render: (_, record) => {
        return <div>{record?.description || "-"}</div>;
      },
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "status",
      render: (_, record) => {
        return <div>{record?.status === "active" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedPackingTable(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([PACKING_TABLE_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <div
                        onClick={() => {
                          openModalForEdit(record);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <span>
                          <EditTwoTone />
                        </span>
                        <span style={{ marginLeft: 5 }}>Edit</span>
                      </div>
                    ),
                    key: "2",
                    disabled: !hasPermission([PACKING_TABLE_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([PACKING_TABLE_CREATE_PERMISSION])}
                addButtonText={hasPermission([PACKING_TABLE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Packing Table Configuration</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllPackingTables}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllPackingTables(filterData, currentPageRef.current)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Packing Table" : "Create Packing Table"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={warehouseForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedPackingTable : createNewPackingTable}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={24}>
              <Form.Item
                name="packing_line_id"
                label="Packing Line"
                rules={[
                  {
                    required: true,
                    message: "Packing Line Required",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Packing Line"
                  onSearch={(value) => {
                    getPackingLines(value);
                  }}
                  onClear={() => getPackingLines()}
                  optionFilterProp="children"
                  filterOption={false}
                >
                  {packingLinesList.map((line) => (
                    <Option key={line.id} value={line.id}>
                      {line.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={24}>
              <Form.Item name="description" placeholder="Description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button type="primary" htmlType="submit" style={{ width: "100%" }} loading={loading}>
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default PackingTableConfig;
